/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

/**
 * A custom element that fetches related query suggestions for the current search term
 * (via Shopify's predictive search JSON API) and renders them as a single line of
 * comma-separated inline links, prefixed by a subdued label.
 *
 * Used on the search results page: <query-suggestions data-search-term="jacket"></query-suggestions>
 */
class QuerySuggestions extends HTMLElement {
  #controller = new AbortController();

  async connectedCallback() {
    const term = this.dataset.searchTerm?.trim();
    if (!term) return;

    const queries = await this.#fetchSuggestions(term, this.#controller.signal);
    if (!this.isConnected) return;

    // Drop the current search term itself (it's not a useful "related" pill).
    const normalizedTerm = term.toLowerCase();
    const filtered = queries.filter((q) => q.text?.toLowerCase().trim() !== normalizedTerm);

    if (filtered.length === 0) return;
    this.#render(filtered);
  }

  /**
   * @param {string} term
   * @param {AbortSignal} signal
   * @returns {Promise<Array<{text: string, url: string, styled_text?: string}>>}
   */
  async #fetchSuggestions(term, signal) {
    const url = new URL('/search/suggest.json', location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'query');
    url.searchParams.set('resources[limit]', '6');

    try {
      const response = await fetch(url.toString(), { signal });
      if (!response.ok) return [];
      const data = await response.json();
      return data.resources?.results?.queries ?? [];
    } catch {
      return [];
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * @param {Array<{text: string, url: string}>} queries
   */
  #render(queries) {
    const linksHtml = queries
      .map((q) => {
        const safeText = this.#escape(q.text);
        const safeUrl = this.#escape(q.url);
        return `<a class="query-suggestions__link" href="${safeUrl}">${safeText}</a>`;
      })
      .join(', ');

    const label = this.#escape(this.dataset.label || 'Related searches');
    this.innerHTML = `<p class="query-suggestions__text"><span class="query-suggestions__label">${label}:</span> ${linksHtml}</p>`;
  }

  /** @param {string} value */
  #escape(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (!customElements.get('query-suggestions')) {
  customElements.define('query-suggestions', QuerySuggestions);
}
